import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { InjectedRouter } from 'react-router';
import type { Simplify } from 'type-fest';

import usePrevious from '../../../hooks/usePrevious';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { nuc } from '../../../utils';
import type { RouterProps } from '../../../types';
import type { PackageInventoryParams } from '../PackageInventoryContainer';

type UseSetFirstPackageActive = Simplify<
  RouterProps<PackageInventoryParams> & {
    router: InjectedRouter;
  }
>;

/**
 * If the user loads the route `/packages` without a packageLocator, this will
 * set one as "active" by applying the locator to the URL.
 *
 * @param {Location} location - window.location
 * @param {string} [params.packageLocator] - The package locator.
 * @param {string} [params.version] - The version, if it exists.
 * @param {InjectedRouter} router - react-router.
 */
const useSetFirstPackageActive = ({ location, params, router }: UseSetFirstPackageActive) => {
  const firstPackage = useSelector(PackageInventoryStore.selectors.getFirstPackage);
  const previousFirstPackage = usePrevious(firstPackage);

  const isInitialLoad =
    !params.packageLocator && // there isn't already a package locator in the URL;
    !params.version && // we're not on the package versions page;
    firstPackage; // packages have loaded and we've got the first one;

  // Detect if the first package changed in the sidebar.
  // This usually indicate that the user navigated away from the page or applied new filters.
  const hasFirstPackageChanged =
    !!previousFirstPackage && !!firstPackage && firstPackage.locator !== previousFirstPackage?.locator;

  useEffect(() => {
    //  Add the package locator param to the URL, but only if...
    if (isInitialLoad || hasFirstPackageChanged) {
      router.replace({
        pathname: `/packages/${nuc(firstPackage.locator)}`,
        search: location.search,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- router is stable, this is fine.
  }, [params.version, params.packageLocator, firstPackage, location.search]);
};

export default useSetFirstPackageActive;
